<script setup lang="ts">
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@laam/ui/base';

interface ProductDescriptionProps {
	description: string;
	productType: string;
	brandName: string;
}

const { $eventClient } = useNuxtApp();

const handleDescriptionClick = () => {
	$eventClient.sendEvent('product-description-click', {
		product_type: props.productType,
		brand_name: props.brandName,
	});
};

const props = defineProps<ProductDescriptionProps>();

const parsingCharacters = (html: string) => {
	const doc = new DOMParser().parseFromString(html, 'text/html');
	return doc.body.textContent || '';
};
const parseDescriptionHTML = () => {
	const description = parsingCharacters(props.description)
		.replace(/(<([^>]+)>)/gi, '')
		.split('\n')
		.filter((line) => line);
	const parsedData: { label?: string; value?: string }[] = [];
	let currentLabel = '';
	let currentValue = '';

	for (const line of description) {
		const label = line?.split(':')[0];
		const value = line?.split(':')[1];

		if (label === 'Additional Description' || label === 'Disclaimer') {
			if (currentLabel) {
				parsedData.push({ label: currentLabel, value: currentValue.trim() });
			}
			currentLabel = label;
			currentValue = value || '';
		} else if (
			currentLabel === 'Additional Description' ||
			currentLabel === 'Disclaimer'
		) {
			currentValue += '\n' + line;
		} else {
			parsedData.push({ label, value });
		}
	}
	if (currentLabel) {
		parsedData.push({ label: currentLabel, value: currentValue.trim() });
	}

	return parsedData;
};

const parsedDescriptionText = parseDescriptionHTML();

const filteredDescriptionText = computed(() => {
	return parsedDescriptionText.filter(
		(item) =>
			item.label !== 'Additional Description' && item.label !== 'Disclaimer',
	);
});
const additionalDescp = computed(() => {
	return parsedDescriptionText.filter(
		(item) =>
			item.label === 'Additional Description' || item.label === 'Disclaimer',
	);
});
</script>

<template>
	<div class="pdp__description gap-xl flex flex-col relative">
		<Accordion
			type="single"
			class="product-description--accordion flex-1 lg:h-full lg:max-h-full"
			default-value="description"
		>
			<AccordionItem value="description" class="rounded-medium border-b-0">
				<AccordionTrigger
					class="p-xl w-full !border-none stroke-none"
					:show-caret="false"
					@click="handleDescriptionClick"
				>
					<div class="gap-md flex items-center">
						<h2 class="text-md font-semibold text-gray-800">Product Details</h2>
					</div>
				</AccordionTrigger>

				<AccordionContent class="p-xl lg:p-3xl pt-none gap-3xl flex flex-col">
					<table>
						<tr
							v-for="(item, index) in filteredDescriptionText.filter(
								(item) => item.label,
							)"
							:key="index"
							:class="{
								'bg-white': index % 2 === 0,
								'bg-gray-100': index % 2 !== 0,
							}"
						>
							<td class="font-normal text-gray-700 text-sm py-sm px-xl w-1/2">
								{{ item.label }}
							</td>
							<td class="font-normal text-gray-900 text-sm py-sm px-xl w-1/2">
								{{ item.value }}
							</td>
						</tr>
					</table>

					<div
						v-for="(item, index) in additionalDescp"
						:key="index"
						class="flex flex-col"
					>
						<span class="font-medium text-sm text-gray-900">
							{{ item.label }}</span>
						<span class="font-normal text-sm text-gray-700">{{
							item.value
						}}</span>
					</div>
				</AccordionContent>
			</AccordionItem>
		</Accordion>
	</div>
</template>
